import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { datumKurz } from "@stammbaum/shared";
import { api, bildUrl, istPdf, type MedienZeile } from "../api";
import { useTitel } from "../titel";

/**
 * Die Medienliste.
 *
 * Bis hierher waren Bilder ausschliesslich ueber eine Person erreichbar.
 * Ein Foto, das an niemandem hing, war damit unsichtbar - es lag in der
 * Datenbank, es lag im Archiv, und man kam nicht hin. Diese Seite ist
 * zuerst einmal der Weg dorthin, und erst danach eine Uebersicht.
 */

const SEITE = 60;

type Sortierung = "aufnahme" | "erfasser" | "ort" | "hochgeladen" | "groesse" | "name";

const SORTIERUNG_LABEL: Record<Sortierung, string> = {
  aufnahme: "Aufnahmedatum",
  erfasser: "Erfasser",
  ort: "Ort",
  hochgeladen: "hochgeladen am",
  groesse: "Dateigröße",
  name: "Titel / Dateiname",
};

/** „8,4 MB", „612 kB". */
function bytesKurz(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1).replace(".", ",")} MB`;
  if (bytes >= 1000) return `${Math.round(bytes / 1000)} kB`;
  return `${bytes} B`;
}

/**
 * Das Datum einer Zeile, mit Herkunft.
 *
 * Beim gepflegten Aufnahmedatum zaehlt die Genauigkeit - aus „um 1950"
 * darf keine Jahreszahl werden, die so aussieht, als wuesste man es genau.
 * Der EXIF-Zeitstempel ist dagegen immer exakt, nur eben bei einem Scan
 * das Datum des Scannens; deshalb steht dort „gescannt" dabei.
 */
function datumZeile(m: MedienZeile): { text: string; herkunft: string | null } {
  if (m.datumQuelle === "aufnahme") {
    return {
      text: datumKurz({
        von: m.aufnahmeVon,
        bis: m.aufnahmeBis,
        genauigkeit: m.aufnahmeGenauigkeit ?? "jahr",
        text: m.aufnahmeText,
      }),
      herkunft: null,
    };
  }
  if (m.datumQuelle === "exif" && m.aufgenommenAm) {
    return { text: new Date(m.aufgenommenAm).toLocaleDateString("de-AT"), herkunft: "aus EXIF" };
  }
  return { text: "ohne Datum", herkunft: null };
}

function Miniatur({ m, groesse }: { m: MedienZeile; groesse: 64 | 160 }) {
  return (
    <span
      className="block shrink-0 overflow-hidden rounded bg-black/10 dark:bg-white/10"
      style={{ width: groesse === 64 ? 40 : undefined, height: groesse === 64 ? 40 : undefined }}
    >
      <img
        src={bildUrl(m.id, groesse)}
        alt=""
        loading="lazy"
        className={groesse === 64 ? "h-full w-full object-cover" : "aspect-square w-full object-cover"}
      />
    </span>
  );
}

export default function Medien() {
  useTitel("Fotos und Dokumente");
  const [suche, setSuche] = useState("");
  const [gebremst, setGebremst] = useState("");
  const [sortierung, setSortierung] = useState<Sortierung>("aufnahme");
  const [richtung, setRichtung] = useState<"auf" | "ab">("ab");
  const [raster, setRaster] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setGebremst(suche), 250);
    return () => clearTimeout(t);
  }, [suche]);

  /**
   * „Mehr laden" hängt echte Seiten aneinander. Das war vorher eine wachsende
   * Obergrenze — bequem, aber beim vierten Klick wäre sie über die 200 des
   * Servers gelaufen und die Liste hätte mit „Eingabe nicht gültig" aufgehört.
   * Ein neuer Suchbegriff ändert den Schlüssel und fängt damit von selbst wieder
   * bei der ersten Seite an.
   */
  const { data, isPending, isFetchingNextPage, hasNextPage, fetchNextPage, error } =
    useInfiniteQuery({
      queryKey: ["medienListe", gebremst, sortierung, richtung],
      queryFn: ({ pageParam }) =>
        api.medienListe({ suche: gebremst, sortierung, richtung, limit: SEITE, offset: pageParam }),
      initialPageParam: 0,
      getNextPageParam: (letzte, alle) => {
        const geladen = alle.reduce((n, s) => n + s.medien.length, 0);
        return geladen < letzte.gesamt && letzte.medien.length > 0 ? geladen : undefined;
      },
      placeholderData: (alt) => alt,
    });

  const medien = data?.pages.flatMap((s) => s.medien) ?? [];
  // `gesamt` von der zuletzt geladenen Seite, nicht von der ersten: laedt
  // jemand nebenbei Bilder hoch, stand sonst „180 von 150" da.
  const gesamt = data?.pages.at(-1)?.gesamt ?? 0;

  const feld =
    "rounded-lg border border-black/15 bg-white px-3 py-2 text-sm outline-none " +
    "focus:border-black/40 dark:border-white/15 dark:bg-neutral-800";

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Medien</h1>
        <span className="text-sm text-black/50 dark:text-white/50">
          {gesamt} {gesamt === 1 ? "Eintrag" : "Einträge"}
          {gebremst && " gefunden"}
        </span>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
          placeholder="suchen … (Titel, Beschreibung, Dateiname, Ort, Personen auf dem Bild)"
          className={feld + " min-w-60 flex-1"}
        />
        <select
          value={sortierung}
          onChange={(e) => setSortierung(e.target.value as Sortierung)}
          className={feld}
        >
          {(Object.keys(SORTIERUNG_LABEL) as Sortierung[]).map((s) => (
            <option key={s} value={s}>
              {SORTIERUNG_LABEL[s]}
            </option>
          ))}
        </select>
        <button
          onClick={() => setRichtung(richtung === "ab" ? "auf" : "ab")}
          title={richtung === "ab" ? "absteigend" : "aufsteigend"}
          className={feld + " hover:border-black/40"}
        >
          {richtung === "ab" ? "↓" : "↑"}
        </button>
        <button onClick={() => setRaster(!raster)} className={feld + " hover:border-black/40"}>
          {raster ? "als Liste" : "als Raster"}
        </button>
      </div>

      {error && <p className="mb-3 text-sm text-red-700">Die Liste konnte nicht geladen werden.</p>}
      {isPending && <p className="text-black/40">lädt …</p>}

      {!isPending && medien.length === 0 && (
        <p className="text-black/50 dark:text-white/50">
          {gebremst ? "Nichts gefunden." : "Noch keine Bilder oder Urkunden."}
        </p>
      )}

      {raster ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {medien.map((m) => {
            const d = datumZeile(m);
            return (
              <Link
                key={m.id}
                to={`/foto/${m.id}`}
                className="group block overflow-hidden rounded-lg border border-black/10 hover:border-black/40
                           dark:border-white/10 dark:hover:border-white/40"
              >
                <Miniatur m={m} groesse={160} />
                <div className="px-2 py-1.5 text-xs">
                  <div className="truncate font-medium">{m.titel || m.dateiname}</div>
                  <div className="truncate text-black/45 dark:text-white/45">
                    {d.text}
                    {m.ortName && ` · ${m.ortName}`}
                  </div>
                  {m.personen.length > 0 && (
                    <div className="truncate text-black/40 dark:text-white/40">
                      {m.personen.map((p) => p.vorname).join(", ")}
                      {m.anzahlPersonen > m.personen.length && " …"}
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      ) : (
        <ul className="divide-y divide-black/5 text-sm dark:divide-white/10">
          {medien.map((m) => {
            const d = datumZeile(m);
            return (
              <li key={m.id}>
                <Link
                  to={`/foto/${m.id}`}
                  className="flex items-center gap-3 py-1.5 hover:bg-black/[0.03] dark:hover:bg-white/5"
                >
                  <Miniatur m={m} groesse={64} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">
                      {m.titel || m.dateiname}
                      {istPdf(m.mimetyp) && (
                        <span className="ml-2 rounded bg-black/10 px-1 text-[10px] uppercase dark:bg-white/15">
                          pdf
                        </span>
                      )}
                    </span>
                    <span className="block truncate text-xs text-black/45 dark:text-white/45">
                      {d.text}
                      {d.herkunft && <span className="ml-1">({d.herkunft})</span>}
                      {m.ortPfad && ` · ${m.ortPfad}`}
                      {m.personen.length > 0 &&
                        ` · ${m.personen.map((p) => `${p.vorname} ${p.famname}`).join(", ")}`}
                      {m.anzahlPersonen > m.personen.length && " …"}
                    </span>
                  </span>
                  <span className="hidden shrink-0 text-xs text-black/40 sm:block dark:text-white/40">
                    {m.erfasser?.anzeigename ?? "—"}
                  </span>
                  <span className="shrink-0 tabular-nums text-xs text-black/40 dark:text-white/40">
                    {bytesKurz(m.bytes)}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {hasNextPage && (
        <div className="mt-4">
          <button
            onClick={() => void fetchNextPage()}
            disabled={isFetchingNextPage}
            className="rounded-lg border border-black/15 px-4 py-2 text-sm hover:border-black/40
                       disabled:opacity-50 dark:border-white/15"
          >
            {isFetchingNextPage
              ? "lädt …"
              : `weitere ${Math.max(1, Math.min(SEITE, gesamt - medien.length))} laden`}
          </button>
          <span className="ml-3 text-sm text-black/40 dark:text-white/40">
            {medien.length} von {gesamt}
          </span>
        </div>
      )}
    </div>
  );
}
